//@flow
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import { SalesCategoryId } from 'common/models/internal/SalesCategoryId';
import { LocationInfo } from 'common/models/internal/LocationInfo';
import * as Immutable from 'immutable';
import I18nHelper from 'common/helpers/I18nHelper';
import PjpImportHelper from 'common/entity/helpers/PjpImportHelper';
import DataUtils from 'common/utils/DataUtils';
import { MasterOutlet } from 'common/models/internal/MasterOutlet';
import { LatLng } from 'common/models/internal/LatLng';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';

const translationsNamespace = ['validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const OutletWeekTypes = Object.freeze({
    Week1: 'WEEK1',
    Week2: 'WEEK2',
    Week3: 'WEEK3',
    Week4: 'WEEK4',
    Week5: 'WEEK5',
    Week6: 'WEEK6',
    Week7: 'WEEK7',
    Week8: 'WEEK8',
    Week9: 'WEEK9',
    Week10: 'WEEK10',
    Week11: 'WEEK11',
    Week12: 'WEEK12',
    Week13: 'WEEK13'
});

export class OutletImportHelper extends EntityImportDataHelper {
    static processMandatoryDays({ cellValue }) {
        return cellValue
            ? cellValue.split(',').map(day => day.trim().toUpperCase())
            : [];
    }

    /**
     * Static
     * Accessor is `["salesCategoryId", "salesCategoryId"]` and
     * in model it is at level `salesCategoryId` so removing one `salesCategoryId`
     * @param {Obejct} {accessor}
     */
    static getSalesCategoryIdAccessor = ({ accessor }: Object) =>
        accessor.pop();

    /**
     * finding `dayOfWeek` `index` in `timeSlots` and `updating` `dayIndex` in accessor's first index
     * @param {Object} { accessor, entity }
     * @static
     */
    static getTimeSlotsAccessor = (accessor: Object) =>
        EntityImportDataHelper.getTimeSlotsAccessor({
            ...accessor,
            workingDaysAccessor: ['timeSlots'],
            dayOfWeekIndex: 1
        });

    static serializeSalesCategoryId = (
        salesCategoryId: string
    ): SalesCategoryId => SalesCategoryId.fromJS({ salesCategoryId });

    static getLocationInfoAccessor = () => {
        return ['locationInfo'];
    };

    static serializeLocationInfo = (value, { entity, cellId }) => {
        const { locationInfo } = entity;
        const { location, locationId } = locationInfo;
        const { header } = cellId;
        let updatedLocation = location;
        let updatedLocationId = locationId;
        if (header.id === 'newLocationId') {
            updatedLocation = location.setName(value).setId(value);
        } else {
            updatedLocationId = locationId.setLocationId(value);
        }

        return LocationInfo.fromJS({
            locationId: updatedLocationId?.toJS(),
            location: updatedLocation?.toJS()
        });
    };

    static validateNewLocationId = ({ entity }) => {
        const { locationId, location } = entity.locationInfo;
        const { id: newLocationId } = location || {};
        const { locationId: existingLocationId } = locationId || {};
        if (!existingLocationId && !newLocationId) {
            return nsTranslate('outletValidationErrors.locationIdMissing');
        }
        if (existingLocationId && newLocationId) {
            return nsTranslate('outletValidationErrors.locationIdPresent');
        }
        if (!existingLocationId && newLocationId) {
            return location.getValidationMessage('id');
        }
        return null;
    };

    static validateLocationType = ({ entity, header }): ?string => {
        const { locationId, location, latLng } = entity.locationInfo;
        const { locationId: existingLocationId } = locationId || {};
        const { address, id: newLocationId, type: locationType } =
            location || {};
        const { lat, lng } = latLng || {};
        if (
            (newLocationId && !existingLocationId && !locationType) ||
            locationType
        ) {
            return location
                .setAllValidationErrors()
                .getValidationMessage('type');
        }
        if (
            (address.localityName ||
                address.formattedAddress ||
                address.city ||
                address.state ||
                address.pincode ||
                address.countryCode ||
                lat ||
                lng) &&
            !locationType &&
            existingLocationId
        ) {
            return nsTranslate('outletValidationErrors.fieldError', {
                field: header.title
            });
        }
    };

    static validateAddressFields = ({ entity, header }) => {
        const { locationId, location, latLng } = entity.locationInfo;
        const { locationId: existingLocationId } = locationId || {};
        const { address, id: newLocationId } = location || {};
        const { lat, lng } = latLng || {};
        const accessor = header.getAccessor();
        const field = accessor.last();
        const value = address.get(field);
        const { type: locationType } = location;
        if (newLocationId && !existingLocationId && !value) {
            return nsTranslate('outletValidationErrors.addressError');
        }
        if (
            (locationType ||
                address.localityName ||
                address.formattedAddress ||
                address.city ||
                address.state ||
                address.pincode ||
                address.countryCode ||
                lat ||
                lng) &&
            !value &&
            existingLocationId
        ) {
            return nsTranslate('outletValidationErrors.fieldError', {
                field: header.title
            });
        }
    };

    static serializeMandatoryDays = (mandatoryDays: Array): Immutable.List => {
        return Immutable.List(mandatoryDays);
    };

    static getCustomFields(entitySettings) {
        return entitySettings?.outletSettings.customFields;
    }

    static processEntityRows = ({ rows }, { getDataForCell }) => {
        const { salesChannels, clientId } = getDataForCell();
        return rows.map(entityRow => {
            const updatedEntityRow = entityRow.map((cellValue, headerId) => {
                let updatedValue = cellValue;
                let riderId = '';
                switch (headerId) {
                    case 'timeSlots':
                        return cellValue
                            .filter(timeSlot => {
                                return timeSlot.slots.length;
                            })
                            .toList();
                    case 'latLng':
                        return LatLng.sanitizeLatLng(cellValue.toJS());
                    case 'customProperties':
                        return cellValue.filter(value => value);
                    case 'channels':
                        return PjpImportHelper.serializeChannels(cellValue, {
                            salesChannels
                        });
                    case 'categories':
                        return cellValue
                            .map((name, id) => ({ id, name }))
                            .toList();
                    case 'salesCategoryId':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('salesCategoryId'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    case 'historicalData':
                        riderId = cellValue && cellValue.get('riderId');
                        updatedValue =
                            riderId &&
                            !DataUtils.isEmpty(riderId.get('riderId'))
                                ? updatedValue
                                : updatedValue.set('riderId', null);
                        return updatedValue;
                    default:
                        return cellValue;
                }
            });
            return MasterOutlet.fromJS(updatedEntityRow.toJS());
        });
    };

    static getDataForCell = ({ clientId, entitySettings }) => {
        const salesChannels = OutletImportHelper.selectSalesChannelsFromEntitySettings(
            entitySettings
        );
        return {
            clientId,
            salesChannels
        };
    };

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }): Immutable.List<MasterOutlet> => {
        const outletsData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: (rowsData, otherProps) =>
                OutletImportHelper.processEntityRows(rowsData, {
                    ...otherProps,
                    getDataForCell
                })
        });
        return Immutable.List(outletsData);
    };

    static generateEntityList = (
        outletsData: Array<*>
    ): Immutable.List<MasterOutlet> =>
        Immutable.List(
            outletsData.map(outletData => MasterOutlet.fromJS(outletData))
        );

    static isInvalidWeek(week) {
        return !DataUtils.isEmpty(week)
            ? !Object.values(OutletWeekTypes).includes(week)
            : false;
    }

    static validateWeek(args) {
        const value = OutletImportHelper.getValue(args);
        return OutletImportHelper.isInvalidWeek(value)
            ? OutletImportHelper.getInvalidOptionError(
                  Object.values(OutletWeekTypes)
              )
            : null;
    }
}

export default OutletImportHelper;
